import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { User, Settings, LayoutDashboard, LogOut } from 'lucide-react';
import { useSocket } from '../../contexts/SocketContext';
import { useAuth } from '../../contexts/AuthContext';
import NewNotificationsMenu from './NewNotificationsMenu';
import NewUserAvatar from './NewUserAvatar';

const NewUserMenu = ({ user, onLogout }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const notificationsRef = useRef(null);
    const { unreadCount } = useSocket();
    const { authStateVersion } = useAuth();

    useEffect(() => {
        console.log('NewUserMenu re-render - Datos del usuario:', {
            user: user
                ? {
                    id: user.id,
                    username: user.username,
                    tickets: user.tickets,
                    role: user.role,
                }
                : null,
            authStateVersion,
            timestamp: new Date().toISOString(),
        });
    }, [user, authStateVersion]);

    useEffect(() => {
        if (user?.tickets !== undefined) {
            console.log(`🎫 Tickets en NewUserMenu actualizados: ${user.tickets}`);
        }
    }, [user?.tickets]);

    // cerrar al click fuera
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
            if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
                setIsNotificationsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="flex items-center gap-4">
            {/* Botón de notificaciones */}
            <div className="relative" ref={notificationsRef}>
                <button
                    onClick={() => setIsNotificationsOpen((v) => !v)}
                    className="group relative flex items-center justify-center w-8 h-8 text-white hover:text-[#fce503] transition-colors"
                    aria-label="Notificaciones"
                >
                    {/* Icono con máscara (se pinta con currentColor) */}
                    <span
                        aria-hidden="true"
                        className="inline-block w-5 h-5 bg-current"
                        style={{
                            WebkitMaskImage: 'url(/images/icons/noti.png)',
                            maskImage: 'url(/images/icons/noti.png)',
                            WebkitMaskRepeat: 'no-repeat',
                            maskRepeat: 'no-repeat',
                            WebkitMaskPosition: 'center',
                            maskPosition: 'center',
                            WebkitMaskSize: 'contain',
                            maskSize: 'contain',
                        }}
                    />

                    {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 flex items-center justify-center w-4 h-4 text-[10px] font-bold text-white bg-red-500 rounded-full">
        {unreadCount > 99 ? '99+' : unreadCount}
      </span>
                    )}
                </button>

                <NewNotificationsMenu
                    isOpen={isNotificationsOpen}
                    onClose={() => setIsNotificationsOpen(false)}
                />
            </div>

            {/* Botón usuario: Avatar + nombre/tickets (abre menú) */}
            <div className="relative" ref={dropdownRef}>
                <button
                    onClick={() => setIsOpen((v) => !v)}
                    className="flex items-center gap-3 text-left"
                    aria-haspopup="menu"
                    aria-expanded={isOpen}
                >
                    <NewUserAvatar user={user} />
                    {/* Desktop: stack vertical con divisor */}
                    <div className="hidden sm:flex flex-col leading-tight min-w-[120px]">
                        <span className="text-[13px] font-semibold text-white">{user.username}</span>
                        <span className="my-1 h-[2px] w-full bg-[#2284DF] rounded-full" />
                        <span className="text-[11px] text-white/80 tracking-wide">
     {user.tickets} <span className="font-semibold text-white">TICKETS</span>
   </span>
                    </div>

                    {/* Flecha */}
                    <svg
                        className={`hidden sm:block w-4 h-4 text-white/80 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                    >
                        <path d="M19 9l-7 7-7-7" />
                    </svg>
                </button>

                {/* Dropdown Mi Cuenta */}
                {isOpen && (
                    <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg py-1 bg-white ring-1 ring-black/5 z-50">
                        <div className="px-4 py-2 text-sm text-primary font-semibold border-b">Mi Cuenta</div>

                        <Link
                            to="/profile"
                            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            onClick={() => setIsOpen(false)}
                        >
                            <User className="w-4 h-4" />
                            <span>Perfil</span>
                        </Link>

                        <Link
                            to="/settings"
                            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            onClick={() => setIsOpen(false)}
                        >
                            <Settings className="w-4 h-4" />
                            <span>Configuración</span>
                        </Link>

                        {user.role === 'ADMIN' && (
                            <a
                                href="/admin"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-4 py-2 text-sm text-primary hover:bg-gray-100"
                                onClick={() => setIsOpen(false)}
                            >
                                <LayoutDashboard className="w-4 h-4" />
                                <span>Dashboard Admin</span>
                            </a>
                        )}

                        <div className="border-t mt-1 pt-1">
                            <button
                                onClick={() => {
                                    setIsOpen(false);
                                    onLogout();
                                }}
                                className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                            >
                                <LogOut className="w-4 h-4" />
                                <span>Cerrar sesión</span>
                            </button>
                        </div>

                        {/* En móvil, acceso rápido a recarga */}
                        <div className="sm:hidden border-t py-2 px-4">
                            <Link
                                to="/recharge"
                                className="flex justify-center w-full px-3 py-2 bg-[#2F86F6] hover:bg-[#4A99FF] text-white font-medium rounded-full text-sm"
                                onClick={() => setIsOpen(false)}
                            >
                                Recargar tickets
                            </Link>
                        </div>
                    </div>
                )}
            </div>

            {/* Botón Recargar tickets (escritorio) */}
            <div className="hidden sm:block">
                <Link
                    to="/recharge"
                    className="px-4 py-2 rounded-lg md:rounded-xl
              text-xs md:text-sm font-extrabold tracking-wide uppercase
              bg-[#2F86F6] hover:bg-[#4A99FF] text-white
              shadow-sm hover:shadow transition-colors"
                >
                    RECARGAR TICKET
                </Link>
            </div>
        </div>
    );
};

export default NewUserMenu;